import type { CSSProperties, ReactNode } from "react";
import Box from "@mui/material/Box";
import MuiTypography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import MuiCircularProgress from "@mui/material/CircularProgress";
import MuiSlider from "@mui/material/Slider";
import MuiAvatar from "@mui/material/Avatar";

// Small antd components with no direct MUI 1:1 -> lightweight wrappers.

// antd <Avatar size shape icon src> -> MUI Avatar (size name -> px, shape -> variant).
export interface AvatarProps {
  size?: "small" | "default" | "large" | number;
  shape?: "circle" | "square";
  src?: string;
  icon?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}
const AVATAR_PX = { small: 24, default: 32, large: 40 } as const;
export const Avatar = ({ size = "default", shape = "circle", src, icon, children, style, className }: AvatarProps) => {
  const dim = typeof size === "number" ? size : AVATAR_PX[size];
  return (
    <MuiAvatar
      src={src}
      variant={shape === "square" ? "rounded" : "circular"}
      sx={{ width: dim, height: dim }}
      style={style}
      className={className}
    >
      {icon ?? children}
    </MuiAvatar>
  );
};

// antd <Statistic title value valueStyle> -> label + value stack.
export interface StatisticProps {
  title?: ReactNode;
  value?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  precision?: number;
  valueStyle?: CSSProperties;
  style?: CSSProperties;
  className?: string;
}
export const Statistic = ({ title, value, prefix, suffix, valueStyle, style, className }: StatisticProps) => (
  <Box style={style} className={className}>
    {title != null && <MuiTypography variant="caption" color="text.secondary" display="block">{title}</MuiTypography>}
    <MuiTypography variant="h6" component="div" style={valueStyle}>
      {prefix}{value}{suffix}
    </MuiTypography>
  </Box>
);

// antd <Affix offsetTop> -> position: sticky.
// alignSelf keeps it content-height inside a flex row: a stretched flex item is as
// tall as the scroll area, so sticky has no slack to slide within and never sticks.
export interface AffixProps { children?: ReactNode; offsetTop?: number; offsetBottom?: number; style?: CSSProperties; className?: string }
export const Affix = ({ children, offsetTop, offsetBottom, style, className }: AffixProps) => (
  <Box sx={{ position: "sticky", top: offsetTop, bottom: offsetBottom, zIndex: 1, alignSelf: "flex-start" }} style={style} className={className}>
    {children}
  </Box>
);

// antd <Progress percent type> -> MUI Linear/Circular progress.
export interface ProgressProps {
  percent?: number;
  type?: "line" | "circle" | "dashboard";
  status?: "success" | "exception" | "active" | "normal";
  showInfo?: boolean;
  strokeColor?: string;
  size?: number | "small" | "default";
  format?: (percent?: number) => ReactNode;
  style?: CSSProperties;
  className?: string;
}
export const Progress = ({ percent = 0, type = "line", showInfo = true, style, className }: ProgressProps) =>
  type === "circle" || type === "dashboard" ? (
    <Box sx={{ position: "relative", display: "inline-flex" }} style={style} className={className}>
      <MuiCircularProgress variant="determinate" value={percent} />
      {showInfo && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MuiTypography variant="caption">{`${Math.round(percent)}%`}</MuiTypography>
        </Box>
      )}
    </Box>
  ) : (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} style={style} className={className}>
      <Box sx={{ flex: 1 }}><LinearProgress variant="determinate" value={percent} /></Box>
      {showInfo && <MuiTypography variant="caption">{`${Math.round(percent)}%`}</MuiTypography>}
    </Box>
  );

// antd <Slider value onChange> -> MUI Slider (onChange(value) signature).
export interface SliderProps {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  onChange?: (value: number) => void;
  marks?: Record<number, ReactNode> | boolean;
  tooltip?: unknown;
  tipFormatter?: (value?: number) => ReactNode;
  style?: CSSProperties;
  className?: string;
}
export const Slider = ({ value, defaultValue, min, max, step, disabled, onChange, style, className }: SliderProps) => (
  <MuiSlider
    value={value}
    defaultValue={defaultValue}
    min={min}
    max={max}
    step={step ?? undefined}
    disabled={disabled}
    onChange={(_e, v) => onChange?.(Array.isArray(v) ? v[0] : v)}
    valueLabelDisplay="auto"
    style={style}
    className={className}
  />
);

// antd <Result status title subTitle extra> -> centered status block.
export interface ResultProps {
  status?: "success" | "error" | "info" | "warning" | "404" | "403" | "500";
  title?: ReactNode;
  subTitle?: ReactNode;
  extra?: ReactNode;
  icon?: ReactNode;
  style?: CSSProperties;
  className?: string;
}
export const Result = ({ title, subTitle, extra, icon, style, className }: ResultProps) => (
  <Box sx={{ textAlign: "center", py: 4 }} style={style} className={className}>
    {icon}
    {title != null && <MuiTypography variant="h5" sx={{ mt: 1 }}>{title}</MuiTypography>}
    {subTitle != null && <MuiTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{subTitle}</MuiTypography>}
    {extra && <Box sx={{ mt: 2 }}>{extra}</Box>}
  </Box>
);
